import { Chip, Divider, Grid, InputAdornment, TextFieldProps, Typography, Box } from '@mui/material';
import MonthPicker from 'components/MonthPicker/MonthPicker';
import PhoneInputField from 'components/PhoneInputField';
import Section from 'components/Section/Section';
import dayjs from 'dayjs';
import { CountryCode } from 'libphonenumber-js';
import { Component, ReactElement } from 'react';
import Autocomplete from '../Autocomplete/Autocomplete';
import Checkbox from '../Checkbox';
import CkEditor5 from '../CkEditor5';
import DateInput from '../DateInput/DateInput';
import DropDownInput from '../DropDownInput';
import InputField, { FieldBelowHint } from '../InputField';
import MultiSelect from '../MultiSelect/MultiSelect';
import RadioInput from '../RadioInput';
import ToggleButtonGroupInput from '../ToggleButtonGroupInput';
import TimeInput, { parseTimeValue } from '../TimeInput/TimeInput';
import GuestCountSplitField from '../GuestCountSplitField';
import Upload from '../Upload/Upload';
import './form.scss';
import AutocompleteFilter from 'components/AutocompleteFilter/AutocompleteFilter';
import strings from 'common/Translation/Translate';
import Currency, { getCurrencyContext } from 'utils/hooks/getCurrency';

/** Live helper for Hold Days: release date = baseDate + holdDays. */
export const buildHoldDaysReleaseHint = (
  holdDaysValue: unknown,
  baseDate: string | Date | number | null | undefined,
): string => {
  if (baseDate == null || baseDate === '') return '';
  if (holdDaysValue === '' || holdDaysValue == null) return '';
  const days = Number(holdDaysValue);
  if (!Number.isFinite(days) || days < 0) return '';

  // API may send epoch millis, ISO string, or yyyy-MM-dd.
  const base =
    typeof baseDate === 'number' || (typeof baseDate === 'string' && /^\d+$/.test(baseDate))
      ? dayjs(Number(baseDate))
      : dayjs(baseDate);
  if (!base.isValid()) return '';

  const release = base.add(days, 'day');
  const formatted = release.format('D MMM YYYY');
  const template = release.isBefore(dayjs(), 'day')
    ? strings.booking_holdDays_release_hint_past
    : strings.booking_holdDays_release_hint;
  return (template || 'Releases on {date}').replace('{date}', formatted);
};

export type GuestVarianceTone = 'positive' | 'negative' | 'neutral';
export type BookedVarianceFormat = 'guests' | 'currency' | 'percent';

const formatGroupedAmount = (amount: number): string => {
  const cur = getCurrencyContext();
  const locale = cur?.currencyLocale?.replace(/_/g, '-') || 'en-IN';
  const abs = Math.abs(amount);
  const hasFraction = Math.abs(abs - Math.round(abs)) > 0.0001;
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: hasFraction ? 2 : 0,
    minimumFractionDigits: 0,
  }).format(abs);
};

const formatSignedGrouped = (delta: number): string => {
  const grouped = formatGroupedAmount(delta);
  if (delta > 0) return `+${grouped}`;
  if (delta < 0) return `-${grouped}`;
  return grouped;
};

const formatPercentNumber = (n: number): string => {
  if (Number.isInteger(n)) return String(n);
  return String(Number(n.toFixed(2)));
};

export const resolveDropdownNumericValue = (
  raw: unknown,
  options?: { value?: any; label?: any }[],
): unknown => {
  if (raw === '' || raw == null || !options?.length) return raw;
  const opt = options.find(o => String(o.value) === String(raw));
  if (!opt) return raw;
  const labelNum = parseFloat(String(opt.label ?? '').replace(/[^\d.-]/g, ''));
  if (Number.isFinite(labelNum)) return labelNum;
  const valueNum = Number(opt.value);
  return Number.isFinite(valueNum) ? valueNum : raw;
};

/** Live helpers: booked baseline below the field + variance chip on the right. */
export const buildBookedVarianceHints = (
  actualValue: unknown,
  bookedValue: number | null | undefined,
  format: BookedVarianceFormat,
): { belowText: string; belowTextRight: string; belowTextRightTone: GuestVarianceTone } => {
  if (bookedValue == null || !Number.isFinite(Number(bookedValue))) {
    return { belowText: '', belowTextRight: '', belowTextRightTone: 'neutral' };
  }
  const booked = Number(bookedValue);
  const bookedDisplay =
    format === 'currency' ? Currency(booked) : format === 'percent' ? `${formatPercentNumber(booked)}%` : String(booked);
  const belowText = String(strings.formatString(strings.actualization_booked_guests, bookedDisplay));

  if (actualValue === '' || actualValue == null) {
    return { belowText, belowTextRight: '', belowTextRightTone: 'neutral' };
  }
  const actual = Number(actualValue);
  if (!Number.isFinite(actual)) {
    return { belowText, belowTextRight: '', belowTextRightTone: 'neutral' };
  }
  const delta = actual - booked;
  if (delta === 0) {
    return {
      belowText,
      belowTextRight: strings.actualization_no_change,
      belowTextRightTone: 'neutral',
    };
  }
  const signed = formatSignedGrouped(delta);
  const belowTextRight =
    format === 'guests'
      ? String(strings.formatString(strings.actualization_guest_variance, signed))
      : format === 'percent'
      ? `${signed}%`
      : signed;
  return {
    belowText,
    belowTextRight,
    belowTextRightTone: delta > 0 ? 'positive' : 'negative',
  };
};

export interface FormValidators {
  check: any;
  message?: string;
  min?: number;
  max?: number;
}

export interface FormModel {
  field: any;
  label: any;
  placeholder: string;
  required: boolean;
  validators: FormValidators[];
  value: any;
  disabled?: boolean;
  styleClass?: string;
  responsive?: any;
  autoFocus?: any;
  variant?: any;
  size?: any;
  inputProps?: any;
  /** Shown inside the field as an end adornment (e.g. '%'). */
  endAdornment?: string;
  /** Compact chip inside the field (e.g. “calculated”). */
  endChip?: string;
  typeValue?: any;
  type?: any;
  options?: any;
  style?: any;
  className?: string;
  sx?: any;
  disablePast?: boolean;
  disableFuture?: boolean;
  minDate?: any;
  maxDate?: any;
  multiline?: boolean;
  showSelectAll?: boolean;
  rows?: number | string;
  minRows?: number | string;
  maxRows?: number | string;
  defaultValue?: any;
  dropdownData?: any;
  isMulti?: boolean;
  isClearable?: boolean;
  handledeleteImage?: (id: number) => void;
  defaultSource?: boolean;
  accept?: string | string[];
  url?: any;
  infoText?: any;
  defaultCountry?: CountryCode;
  selected?: boolean;
  belowText?: string;
  /** Chip on the helper row, right-aligned (used when bookedBaseline is not set). */
  belowTextRight?: string;
  belowTextRightTone?: GuestVarianceTone;
  /**
   * Base date (ISO / Date / dayjs-parsable) for holdDays release hint.
   * When set on the holdDays field, Form shows a live “Releases on …” line under the input.
   */
  holdReleaseBaseDate?: string | Date | null;
  /** Style belowText with warning icon + color. */
  belowTextWarning?: boolean;
  /** Booked baseline used to show “Booked: …” below the field and a live variance chip. */
  bookedBaseline?: number | null;
  bookedVarianceFormat?: BookedVarianceFormat;
  /** Shown beside the field (same row), e.g. Hold Days release hint. */
  besideText?: string;
  besideTextWarning?: boolean;
  children?: React.ReactNode;
  compactSection?: boolean;
  /** Optional icon shown before a section header label. */
  sectionIcon?: React.ReactNode;
  /** For guest-count-split: auto-calculated expected guests label context. */
  excessGuestsPercentage?: number | null;
  venueMaxCapacity?: number | null;
  venueName?: string | null;
  layoutMaxCapacity?: number | null;
  layoutName?: string | null;
  layoutSelected?: boolean;
  expectedGuestsLabel?: string;
  /** Shown in Autocomplete when the options list is empty. */
  noOptionsText?: string;
}

export interface FormDataModel {
  [key: string]: any;
}

interface FormProps {
  isFormUpdated?: boolean;
  values: any;
  model: FormModel[];
  strings?: any;
  testId?: string;
  onChange?: (field: string, value: string | boolean, formData: FormDataModel, deleted?: any) => void;
  onFocus?: (field: string, value: string | boolean, formData: FormDataModel, deleted?: any) => void;
  card?: any;
  hasError?: boolean;
  fieldError?: boolean;
  dropdownData?: any;
}

export default class Form extends Component<FormProps> {
  state: { formData: FormDataModel; isFormValid: boolean } = {
    formData: {},
    isFormValid: false,
  };

  private determinePartOfDay = (timeValue: string | null | undefined) => {
    if (!timeValue || typeof timeValue !== 'string') return '';
    // Expecting HH:mm or HH:mm:ss
    const match = timeValue.match(/^(\d{2}):(\d{2})/);
    if (!match) return '';
    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const totalMinutes = hours * 60 + minutes;
    // DINNER 00:00 - 05:59
    if (totalMinutes >= 0 && totalMinutes < 6 * 60) return 'DINNER';
    // BREAKFAST 06:00 - 09:59
    if (totalMinutes >= 6 * 60 && totalMinutes < 10 * 60) return 'BREAKFAST';
    // LUNCH 10:00 - 14:59
    if (totalMinutes >= 10 * 60 && totalMinutes < 15 * 60) return 'LUNCH';
    // HI_TEA 15:00 - 17:59
    if (totalMinutes >= 15 * 60 && totalMinutes < 18 * 60) return 'HI_TEA';
    // DINNER 18:00 - 23:59
    if (totalMinutes >= 18 * 60 && totalMinutes < 24 * 60) return 'DINNER';
    return '';
  };

  componentDidMount() {
    this.prepareFormData();
  }

  componentDidUpdate(prevProps: Readonly<FormProps>) {
    const { values, strings } = this.props;
    if (
      this.props.isFormUpdated !== prevProps.isFormUpdated ||
      strings !== prevProps.strings ||
      (values && Object.keys(values).length > 0 && values !== prevProps.values)
    ) {
      this.prepareFormData();
    }
  }

  handleChange = (
    value: string | boolean,
    field: string,
    error?: { error: boolean; message: string },
    deleted?: any,
  ) => {
    const formData: any = this.state.formData;

    formData[field] = value;
    if (deleted?.deletedField === field) {
      formData[field + 'deleted'] = deleted?.DeletedFile;
    }
    formData[field + 'Error'] = error && error.error;
    // if (field === 'partOfDay') {
    //   const hasValue = typeof value === 'string' && value !== '';
    //   formData['partOfDayUserOverride'] = hasValue;
    // }

    // if (field === 'startTime' && !formData['partOfDayUserOverride']) {
    //   let timeText = '';
    //   if (typeof value === 'string') {
    //     timeText = value;
    //   } else if (value && (value as any).format) {
    //     try {
    //       timeText = (value as any).format('HH:mm');
    //     } catch (_) {
    //       timeText = '';
    //     }
    //   }
    //   const part = this.determinePartOfDay(timeText);
    //   if (part) {
    //     formData['partOfDay'] = part;
    //     formData['partOfDayError'] = false;
    //   }
    // }
    this.setState({
      formData,
      isFormValid: this.validateForm(formData),
    });
    if (this.props.onChange) {
      this.props.onChange(field, value, formData, deleted);
    }
  };
  handleFocus = (value: string | boolean, field: string, deleted?: any) => {
    const formData: any = this.state.formData;

    if (this.props.onFocus) {
      this.props.onFocus(field, value, formData, deleted);
    }
  };

  validateForm = (formData: FormDataModel) => {
    const { model } = this.props;
    let isFormValid = true;
    model.forEach(item => {
      if (item.required || formData[item.field + 'Error']) {
        isFormValid = isFormValid && !formData[item.field + 'Error'];
      }
    });
    return isFormValid;
  };

  getFormData = () => {
    const { formData, isFormValid } = this.state;
    return { formData, isFormValid };
  };

  resetForm = () => {
    this.prepareFormData();
  };
  prepareFormDataHelper3 = (item: any, values: any, formData: FormDataModel) => {
    const hasValue = (v: unknown) => v === 0 || (v != null && v !== '');
    if (hasValue(formData[item.field]) || !item.required) {
      formData[item.field + 'Error'] =
        hasValue(values[item.field]) && values[item.field && item.field !== ''] && item.required ? true : false;
    } else {
      formData[item.field + 'Error'] =
        hasValue(values[item.field]) && item.required ? false : true;
    }
  };
  prepareFormDataHelper1 = (model: FormModel[], values: any, formData: FormDataModel) => {
    model.forEach(item => {
      formData[item.field] =
        values[item.field] === 0 || (values[item.field] != null && values[item.field] !== '')
          ? values[item.field]
          : '';
      this.prepareFormDataHelper3(item, values, formData);
    });
    this.setState({ formData, isFormValid: this.validateForm(formData) });
  };

  prepareFormDataHelper2 = (model: FormModel[], values: any, formData: FormDataModel) => {
    model.forEach(item => {
      formData[item.field] =
        values && (values[item.field] === 0 || values[item.field]) ? values[item.field] : '';
      formData[item.field + 'Error'] = item.required;
    });

    this.setState({ formData, isFormValid: this.validateForm(formData) });
  };

  prepareFormData() {
    const { model, values } = this.props;
    const formData: FormDataModel = {};
    const isStoredTimeInvalid = (time: unknown) => {
      if (time == null || time === '') return false;
      if (dayjs.isDayjs(time)) return !time.isValid();
      if (typeof time === 'string') return parseTimeValue(time) == null;
      return dayjs(time as any).format() === 'Invalid Date';
    };
    if (values && Object.keys(values).length !== 0) {
      let modifiedValues = { ...values };
      if (values.startTime && isStoredTimeInvalid(values.startTime)) {
        modifiedValues = { ...modifiedValues, startTime: '' };
      }
      if (values.endTime && isStoredTimeInvalid(values.endTime)) {
        modifiedValues = { ...modifiedValues, endTime: '' };
      }
      this.prepareFormDataHelper1(model, modifiedValues, formData);

      // if (formData['startTime'] && !formData['partOfDay'] && !formData['partOfDayUserOverride']) {
      //   let timeText = '';
      //   const startVal = formData['startTime'] as any;
      //   if (typeof startVal === 'string') {
      //     timeText = startVal;
      //   } else if (startVal && startVal.format) {
      //     try {
      //       timeText = startVal.format('HH:mm');
      //     } catch (_) {
      //       timeText = '';
      //     }
      //   }
      //   const part = this.determinePartOfDay(timeText);
      //   if (part) {
      //     const next = { ...this.state.formData, ...formData, partOfDay: part, partOfDayError: false };
      //     this.setState({ formData: next, isFormValid: this.validateForm(next) });
      //     return;
      //   }
      // }
    } else {
      this.prepareFormDataHelper2(model, values, formData);
    }
  }

  renderFormFields() {
    const { model, hasError, dropdownData, values } = this.props;
    const { formData } = this.state;
    const arrayOfFields: ReactElement[] = [];
    const fieldActualRaw = (item: FormModel) =>
      formData[item.field] !== undefined && formData[item.field] !== ''
        ? formData[item.field]
        : values?.[item.field];
    const fieldVarianceHints = (item: FormModel) => {
      if (item.bookedBaseline == null || !item.bookedVarianceFormat) return null;
      const raw = fieldActualRaw(item);
      const actual = item.type === 'drop-down' ? resolveDropdownNumericValue(raw, item.options) : raw;
      return buildBookedVarianceHints(actual, item.bookedBaseline, item.bookedVarianceFormat);
    };
    model.forEach((item, key) => {
      // Layout rows like sections / dividers carry no `field`, which would collide on
      // React's key check whenever a model has more than one of them. Fall back to a
      // type+index suffix so every rendered Grid keeps a stable, unique key.
      const rowKey: string = item.field ? String(item.field) : `${item.type || 'row'}-${key}`;
      const { required, label, placeholder } = item;
      const customLabel = required ? `${label} *` : label;
      const a = required ? `${placeholder} *` : placeholder;
      const customPlaceholder = placeholder === '' ? '' : a;
      const fieldSize = !item.size || item.size === 'medium' ? 'small' : item.size;

      switch (item.type) {
        case 'text': {
          const holdDaysRaw =
            formData['holdDays'] !== undefined && formData['holdDays'] !== ''
              ? formData['holdDays']
              : values?.holdDays;
          const holdDaysHint =
            item.field === 'holdDays' && item.holdReleaseBaseDate
              ? buildHoldDaysReleaseHint(holdDaysRaw, item.holdReleaseBaseDate)
              : '';
          const varianceHints = fieldVarianceHints(item);
          arrayOfFields.push(
            <Grid key={rowKey} {...item.responsive} item className={'form-group ' + item.styleClass}>
              <InputField
                disabled={item.disabled || false}
                autoFocus={item.autoFocus || false}
                variant={item.variant}
                size={fieldSize}
                inputProps={item.inputProps || {}}
                InputProps={
                  item.endAdornment || item.endChip
                    ? {
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            sx={{ '& .MuiTypography-root': { fontSize: '0.875rem' }, mr: item.endChip ? -0.5 : 0 }}>
                            {item.endChip ? (
                              <Chip
                                size="small"
                                label={item.endChip}
                                sx={{
                                  height: 18,
                                  fontSize: 10,
                                  fontWeight: 600,
                                  borderRadius: '9px',
                                  bgcolor: 'action.hover',
                                  color: 'text.secondary',
                                  '& .MuiChip-label': { px: 0.75, py: 0 },
                                }}
                              />
                            ) : (
                              <Typography color="text.secondary" component="span">
                                {item.endAdornment}
                              </Typography>
                            )}
                          </InputAdornment>
                        ),
                      }
                    : undefined
                }
                hasError={hasError || false}
                field={item.field}
                multiline={item.multiline}
                rows={item.rows}
                minRows={item.minRows}
                maxRows={item.maxRows}
                inputValue={
                  formData[item.field] || formData[item.field] === 0
                    ? (formData[item.field] as string)
                    : item.value || item.value === 0
                    ? item.value
                    : ''
                }
                style={item.style}
                typeValue={item.typeValue || ''}
                label={customLabel || ''}
                fieldError={item.field ? (formData[item.field + 'Error'] as boolean) : false}
                validators={item.validators}
                className={item.className}
                textChange={this.handleChange}
                sx={item.sx}
                infoText={item.infoText}
                belowText={holdDaysHint ? '' : varianceHints ? varianceHints.belowText : item.belowText || ''}
                belowTextRight={
                  holdDaysHint ? '' : varianceHints?.belowTextRight || item.belowTextRight || ''
                }
                belowTextRightTone={varianceHints?.belowTextRightTone ?? item.belowTextRightTone}
                besideText={holdDaysHint || item.besideText || ''}
                besideTextWarning={item.besideTextWarning || !!holdDaysHint}
              />
            </Grid>,
          );
          break;
        }
        case 'checkbox':
          arrayOfFields.push(
            <Grid key={rowKey} {...item.responsive} item className={'form-group ' + item.styleClass}>
              <Checkbox
                label={customLabel}
                field={item.field}
                value={formData[item.field] ? formData[item.field] : false}
                // selected={item.selected}
                disabled={item.disabled}
                textChange={this.handleChange}
                sx={item.sx}
              />
            </Grid>,
          );
          break;
        case 'upload':
          arrayOfFields.push(
            <Grid key={rowKey} {...item.responsive} item className={'form-group ' + item.styleClass}>
              <Upload
                inputProps={item.inputProps || {}}
                hasError={hasError || false}
                field={item.field}
                accept={item.accept}
                multiline={item.multiline}
                // handledeleteImage={item.handledeleteImage}
                inputValue={formData[item.field] || formData[item.field] === 0 ? formData[item.field] : []}
                style={item.style}
                typeValue={item.typeValue || ''}
                label={customLabel || ''}
                fieldError={item.field ? (formData[item.field + 'Error'] as boolean) : false}
                validators={item.validators}
                textChange={this.handleChange}
                sx={item.sx}
              />
            </Grid>,
          );
          break;
        case 'drop-down': {
          const varianceHints = fieldVarianceHints(item);
          arrayOfFields.push(
            <Grid key={rowKey} {...item.responsive} item className={'form-group ' + item.styleClass}>
              <Box sx={item.sx}>
                <DropDownInput
                  sx={{}}
                  options={item.options}
                  className={''}
                  id={''}
                  style={item.style}
                  labelId={''}
                  disabled={item.disabled || false}
                  inputProps={item.inputProps || {}}
                  hasError={hasError || false}
                  field={item.field}
                  inputValue={formData[item.field] || formData[item.field] === 0 ? (formData[item.field] as string) : ''}
                  placeholder={customPlaceholder}
                  label={customLabel || ''}
                  fieldError={item.field ? (formData[item.field + 'Error'] as boolean) : false}
                  validators={item.validators}
                  onChange={this.handleChange}
                  onFocus={this.handleFocus}
                  size={fieldSize}
                  isClearable={item.isClearable}
                  noOptionsText={item.noOptionsText}
                />
                <FieldBelowHint
                  belowText={varianceHints?.belowText}
                  belowTextRight={varianceHints?.belowTextRight}
                  belowTextRightTone={varianceHints?.belowTextRightTone}
                />
              </Box>
            </Grid>,
          );
          break;
        }
        case 'select':
          arrayOfFields.push(
            <Grid key={rowKey} {...item.responsive} item className={'form-group ' + item.styleClass}>
              <MultiSelect
                options={item.options ?? dropdownData?.[item.field]}
                className={''}
                id={''}
                showSelectAll={item.showSelectAll}
                style={item.style}
                isClearable={item.isClearable}
                field={item.field}
                placeholder={customPlaceholder}
                inputValue={formData[item.field] || formData[item.field] === 0 ? (formData[item.field] as string) : ''}
                labelId={''}
                isMulti={item.isMulti}
                disabled={item.disabled || false}
                inputProps={item.inputProps || {}}
                hasError={hasError || false}
                label={customLabel ?? ''}
                fieldError={item.field ? (formData[item.field + 'Error'] as boolean) : false}
                validators={item.validators}
                onChange={this.handleChange}
              />
            </Grid>,
          );
          break;
        case 'textEditor':
          arrayOfFields.push(
            <Grid key={rowKey} {...item.responsive} item className={'form-group ' + item.styleClass}>
              <CkEditor5
                hasError={hasError || false}
                fieldError={item.field ? (formData[item.field + 'Error'] as boolean) : false}
                validators={item.validators}
                label={customLabel}
                field={item.field}
                textChange={this.handleChange}
                value={formData[item.field] ? formData[item.field] : ''}
                defaultSource={item.defaultSource}
                disabled={item.disabled || false}
                placeholder={item.placeholder || undefined}
              />
            </Grid>,
          );
          break;
        case 'section':
          arrayOfFields.push(
            <Grid
              key={rowKey}
              {...item.responsive}
              item
              className={`form-group form-group-section ${item.styleClass || ''}`.trim()}
              sx={{ pt: item.compactSection === false ? 2 : 0, pb: 0 }}>
              <Section
                label={customLabel}
                field={item.field}
                compact={item.compactSection !== false}
                icon={item.sectionIcon}
              />
            </Grid>,
          );
          break;
        case 'custom':
          arrayOfFields.push(
            <Grid key={rowKey} {...item.responsive} item className={'form-group ' + item.styleClass}>
              {item.children}
            </Grid>,
          );
          break;
        case 'divider':
          arrayOfFields.push(
            <Grid key={rowKey} {...item.responsive} item className={'form-group ' + item.styleClass}>
              <Divider
                variant={item.variant ?? 'fullWidth'}
                textAlign={'left'}
                sx={{
                  color: 'primary.main',
                  '&::before, &::after': {
                    borderColor: 'primary.main',
                  },
                }}>
                <Typography variant={item.size ?? 'subtitle1'} color={'primary'} sx={{ fontWeight: 700 }}>
                  {customLabel}
                </Typography>
              </Divider>
            </Grid>,
          );
          break;
        case 'radio':
          arrayOfFields.push(
            <Grid key={rowKey} {...item.responsive} item className={'form-group ' + item.styleClass}>
              <RadioInput
                options={item.options}
                className={''}
                id={''}
                style={item.style}
                labelId={''}
                defaultValue={
                  formData[item.field] || formData[item.field] === 0 ? (formData[item.field] as string) : false
                }
                disabled={item.disabled || false}
                inputProps={item.inputProps || {}}
                hasError={hasError || false}
                field={item.field}
                inputValue={
                  formData[item.field] || formData[item.field] === 0 ? (formData[item.field] as string) : false
                }
                placeholder={customPlaceholder}
                label={customLabel || ''}
                fieldError={item.field ? (formData[item.field + 'Error'] as boolean) : false}
                validators={item.validators}
                onChange={this.handleChange}
                size={fieldSize}
              />
            </Grid>,
          );
          break;
        case 'toggle-button-group':
          arrayOfFields.push(
            <Grid key={rowKey} {...item.responsive} item className={'form-group ' + item.styleClass}>
              <ToggleButtonGroupInput
                options={item.options}
                disabled={item.disabled || false}
                hasError={hasError || false}
                field={item.field}
                inputValue={
                  formData[item.field] || formData[item.field] === 0 ? (formData[item.field] as string) : false
                }
                fieldError={item.field ? (formData[item.field + 'Error'] as boolean) : false}
                validators={item.validators}
                onChange={this.handleChange}
              />
            </Grid>,
          );
          break;
        case 'date':
          arrayOfFields.push(
            <Grid key={rowKey} {...item.responsive} item className={'form-group ' + item.styleClass}>
              <DateInput
                label={customLabel}
                validators={item.validators}
                textChange={this.handleChange}
                value={
                  formData[item.field] || formData[item.field] === 0
                    ? (formData[item.field] as string)
                    : null
                }
                field={item.field}
                size={fieldSize}
                onChange={e => e}
                placeholder={customPlaceholder}
                hasError={hasError || false}
                maxDate={item.maxDate}
                disabled={item.disabled}
                disablePast={item.disablePast}
                disableFuture={item.disableFuture}
                fieldError={item.field ? (formData[item.field + 'Error'] as boolean) : false}
                minDate={item.minDate}
                renderInput={(params: TextFieldProps) => (
                  <InputField
                    size={fieldSize}
                    fieldError={item.field ? (formData[item.field + 'Error'] as boolean) : false}
                    inputValue={params.value as string}
                    hasError={hasError || false}
                    {...params}
                  />
                )}
              />
            </Grid>,
          );
          break;
        case 'month':
          arrayOfFields.push(
            <Grid key={rowKey} {...item.responsive} item className={'form-group ' + item.styleClass}>
              <MonthPicker
                label={customLabel}
                validators={item.validators}
                textChange={this.handleChange}
                value={
                  formData[item.field] || formData[item.field] === 0
                    ? (formData[item.field] as string)
                    : null
                }
                field={item.field}
                size={fieldSize}
                onChange={e => e}
                disablePast={item.disablePast}
                disableFuture={item.disableFuture}
                placeholder={customPlaceholder}
                hasError={hasError || false}
                fieldError={item.field ? (formData[item.field + 'Error'] as boolean) : false}
                minDate={item.minDate}
                maxDate={item.maxDate}
                disabled={item.disabled}
                renderInput={(params: TextFieldProps) => {
                  const { error: _pickerError, helperText: _pickerHelperText, ...pickerParams } = params;
                  return (
                    <InputField
                      size={fieldSize}
                      hasError={hasError || false}
                      inputValue={params.value as string}
                      fieldError={item.field ? (formData[item.field + 'Error'] as boolean) : false}
                      {...pickerParams}
                    />
                  );
                }}
              />
            </Grid>,
          );
          break;
        case 'guest-count-split':
          arrayOfFields.push(
            <Grid key={rowKey} {...item.responsive} item className={'form-group ' + item.styleClass}>
              <GuestCountSplitField
                guestsLabel={label}
                expectedLabel={item.expectedGuestsLabel || strings.venueDetailsTabsExpected}
                field={item.field}
                guestsValue={
                  formData[item.field] || formData[item.field] === 0 ? (formData[item.field] as string | number) : ''
                }
                disabled={item.disabled}
                hasError={hasError || false}
                fieldError={item.field ? (formData[item.field + 'Error'] as boolean) : false}
                layoutMaxCapacity={item.layoutMaxCapacity}
                layoutName={item.layoutName}
                layoutSelected={item.layoutSelected}
                venueMaxCapacity={item.venueMaxCapacity}
                venueName={item.venueName}
                excessGuestsPercentage={item.excessGuestsPercentage}
                textChange={this.handleChange}
                size={fieldSize}
              />
            </Grid>,
          );
          break;
        case 'time':
          arrayOfFields.push(
            <Grid key={rowKey} {...item.responsive} item className={'form-group ' + item.styleClass}>
              <TimeInput
                label={customLabel}
                validators={item.validators}
                textChange={this.handleChange}
                value={formData[item.field] || formData[item.field] === 0 ? formData[item.field] : null}
                field={item.field}
                size={fieldSize}
                placeholder={customPlaceholder}
                hasError={hasError || false}
                fieldError={item.field ? (formData[item.field + 'Error'] as boolean) : false}
                minDate={item.minDate}
                disabled={item.disabled}
              />
            </Grid>,
          );
          break;
        case 'autocomplete':
          arrayOfFields.push(
            <Grid key={rowKey} {...item.responsive} item className={'form-group ' + item.styleClass}>
              <Autocomplete
                sx={item.sx}
                url={item.url}
                id={''}
                disabled={item.disabled || false}
                hasError={hasError || false}
                field={item.field}
                value={formData[item.field] || formData[item.field] === 0 ? (formData[item.field] as string) : ''}
                placeholder={customPlaceholder}
                label={customLabel ?? ''}
                fieldError={item.field ? (formData[item.field + 'Error'] as boolean) : false}
                validators={item.validators}
                onChange={this.handleChange}
                size={fieldSize}
              />
            </Grid>,
          );
          break;
        case 'autocompleteFilter':
          arrayOfFields.push(
            <Grid key={rowKey} {...item.responsive} item className={'form-group ' + item.styleClass}>
              <AutocompleteFilter
                sx={item.sx}
                url={item.url}
                id={''}
                disabled={item.disabled || false}
                hasError={hasError || false}
                field={item.field}
                value={formData[item.field] || formData[item.field] === 0 ? (formData[item.field] as string) : ''}
                placeholder={customPlaceholder}
                label={customLabel ?? ''}
                fieldError={item.field ? (formData[item.field + 'Error'] as boolean) : false}
                validators={item.validators}
                onChange={this.handleChange}
                size={fieldSize}
              />
            </Grid>,
          );
          break;
        case 'phoneInput':
          arrayOfFields.push(
            <Grid key={rowKey} {...item.responsive} item className={'form-group ' + item.styleClass}>
              <PhoneInputField
                defaultCountry={item.defaultCountry || 'IN'}
                disabled={item.disabled || false}
                autoFocus={item.autoFocus || false}
                variant={item.variant}
                size={fieldSize}
                inputProps={item.inputProps || {}}
                hasError={hasError || false}
                field={item.field}
                inputValue={formData[item.field] || { value: '', countryCode: null }}
                style={item.style}
                label={customLabel || ''}
                fieldError={item.field ? (formData[item.field + 'Error'] as boolean) : false}
                validators={item.validators}
                className={item.className}
                textChange={this.handleChange}
                sx={item.sx}
                required={item.required}
              />
            </Grid>,
          );
          break;
        case 'sonarFix':
          arrayOfFields.push(<>{1 || this.resetForm() || this.getFormData()}</>);
          break;
        default:
          break;
      }
    });

    return arrayOfFields;
  }

  render() {
    return <>{this.renderFormFields()}</>;
  }
}
